import { Component, OnInit, ViewChild } from '@angular/core';
import { DeprecatedModalFormComponent } from '@app/components';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { BenefitPolicy } from '@app/models/benefits/benefit-policy.model';
import { AnalyticService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { getHelpCentreArticle } from '@app/types/translatable.type';
import { BenefitsMenuService } from '@benefits/services/benefits-menu.service';
import { PlansIndexTable } from '@benefits/services/tables/plans-table/plans-index.table';
import { IndexView } from '@common/views/index/index.view';
import { BaseForm } from '@forms/base.form';
import { BenefitPlan } from '@models/benefits/benefit-plan.model';

@Component({
    templateUrl: './plan-index.template.html',
    styleUrls: ['./plan-index.style.scss'],
})
export class PlanIndexView extends IndexView implements OnInit {
    @ViewChild('createModal', { static: true }) createModal!: DeprecatedModalFormComponent;

    isLoading = false;
    showCreateButton = false;
    learnMoreUrl = getHelpCentreArticle('4410036309783');

    canAccessBenefitsSettings = false;

    constructor(
        private notify: NotifyService,
        private auth: AuthService,
        private analyticService: AnalyticService,
        public table: PlansIndexTable,
        public readonly benefitsMenuService: BenefitsMenuService,
        private featureService: FeatureService
    ) {
        super();
        this.table.reload();
    }

    async ngOnInit(): Promise<void> {
        this.canAccessBenefitsSettings = await this.canAccessBenefitSettings();
        this.verifyBenefitPolicy();
    }

    async verifyBenefitPolicy(): Promise<void> {
        this.isLoading = true;
        const [benefitPolicies] = await BenefitPolicy.param('company', this.auth.company.id).get();

        if (benefitPolicies.find((e) => e.hasEnhancedManagedBenefits === true)) {
            this.showCreateButton = false;
            this.isLoading = false;
            return;
        }

        this.showCreateButton = true;
        this.isLoading = false;
    }

    onCreate(benefitPlan: BenefitPlan, form: BaseForm): void {
        benefitPlan
            .param('company', this.auth.company.id)
            .save()
            .then(() => {
                form.reset();
                this.table.reload();
            })
            .catch(() => this.notify.error('Unable to create benefits plan'));
    }

    onCreatePlan(): void {
        this.analyticService.trackEvent(AnalyticEvents.SelfServeLandingPageAddPlan);
        this.createModal.show();
    }

    onLearnMoreClick(): void {
        this.analyticService.trackEvent(AnalyticEvents.SelfServeLandingPageLearnMore);
    }

    private async canAccessBenefitSettings(): Promise<boolean> {
        if (!(await this.featureService.has(FeatureFlag.benefitsPayroll))) {
            return false;
        }

        return this.auth.company.isBenefitsClient;
    }
}
